import type { MetaFunction } from 'react-router'
import { STATIC_PAGE_HEADERS } from "~/lib/cache.server"
import { Link } from 'react-router'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { SERVICE_LABELS, EMERGENCY_SERVICES } from '~/lib/programmatic-seo'
import { AlertTriangle, PhoneCall, Clock, Shield, BadgeCheck, MapPin } from 'lucide-react'

export function headers() { return STATIC_PAGE_HEADERS }

export const meta: MetaFunction = () => [
  { title: 'Urgence travaux — Artisans disponibles 7j/7 | AfficheServices' },
  { name: 'description', content: 'Besoin d\'un artisan en urgence ? Plombier, électricien, serrurier — intervention rapide 7j/7 dans toute la France. Artisans vérifiés, devis avant intervention.' },
  { tagName: 'link', rel: 'canonical', href: 'https://www.afficheservices.fr/urgence' },
  { property: 'og:image', content: 'https://www.afficheservices.fr/api/og?title=Urgence+travaux&subtitle=Artisans+7j/7' },
]

const TOP_CITIES = [
  'Paris', 'Lyon', 'Marseille', 'Toulouse', 'Bordeaux', 'Nantes',
  'Nice', 'Strasbourg', 'Lille', 'Montpellier', 'Rennes', 'Grenoble',
  'Rouen', 'Toulon', 'Saint-Étienne', 'Le Havre', 'Dijon', 'Angers',
  'Nîmes', 'Clermont-Ferrand',
]

const EMERGENCY_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  plomberie: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  electricite: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  serrurerie: { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  toiture: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  climatisation: { bg: 'bg-cyan-50', text: 'text-cyan-700', border: 'border-cyan-200' },
  assainissement: { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  'couverture-zinguerie': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  nettoyage: { bg: 'bg-lime-50', text: 'text-lime-700', border: 'border-lime-200' },
}

function slugify(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-')
}

export default function UrgenceIndex() {
  const emergencyServices = EMERGENCY_SERVICES
    .map((slug) => ({ slug, ...SERVICE_LABELS[slug] }))
    .filter((s) => s.name)

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      {/* Hero */}
      <div className="bg-gradient-to-br from-red-600 to-orange-500">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center text-white lg:px-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-white/20 backdrop-blur-sm">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h1 className="mt-4 text-3xl font-bold sm:text-4xl">Urgence travaux</h1>
          <p className="mt-3 text-lg text-red-100">
            Artisans vérifiés disponibles 7j/7 pour vos urgences
          </p>
          <div className="mt-6 flex flex-col items-center gap-3">
            <a
              href="tel:+33743019662"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-bold text-red-600 shadow-lg hover:bg-slate-50"
            >
              <PhoneCall className="h-5 w-5" /> Appeler maintenant — 07 43 01 96 62
            </a>
            <Link
              to="/demande-devis/1"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 underline-offset-4 hover:underline"
            >
              Ou demander un rappel sous 30 min
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-6">
            <div className="flex items-center gap-1.5 text-sm text-red-100">
              <Clock className="h-4 w-4" /> Intervention sous 1h
            </div>
            <div className="flex items-center gap-1.5 text-sm text-red-100">
              <Shield className="h-4 w-4" /> Devis avant travaux
            </div>
            <div className="flex items-center gap-1.5 text-sm text-red-100">
              <BadgeCheck className="h-4 w-4" /> Artisans assurés
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-4 py-12 lg:px-6">

        {/* Emergency services grid */}
        <h2 className="text-xl font-bold text-slate-900">Nos services d'urgence</h2>
        <p className="mt-2 text-sm text-slate-500">Sélectionnez un service et votre ville pour trouver un artisan disponible immédiatement.</p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {emergencyServices.map((svc) => {
            const colors = EMERGENCY_COLORS[svc.slug] ?? { bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' }
            return (
              <div key={svc.slug} className={`rounded-2xl border ${colors.border} ${colors.bg} p-5`}>
                <h3 className={`text-base font-bold ${colors.text}`}>
                  Urgence {svc.name?.toLowerCase()}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {svc.artisan?.charAt(0).toUpperCase()}{svc.artisan?.slice(1)} en urgence — intervention rapide 7j/7
                </p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {TOP_CITIES.slice(0, 8).map((city) => (
                    <Link
                      key={city}
                      to={`/urgence/${slugify(svc.artisan ?? svc.slug)}-${slugify(city)}`}
                      className="rounded-full border border-white/80 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 hover:border-red-300 hover:text-red-600"
                    >
                      {city}
                    </Link>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        {/* All cities × services matrix */}
        <div className="mt-12">
          <h2 className="text-xl font-bold text-slate-900">Toutes les villes</h2>
          <p className="mt-2 text-sm text-slate-500">Trouvez un artisan urgentiste dans votre ville</p>

          <div className="mt-6 space-y-6">
            {TOP_CITIES.map((city) => (
              <div key={city}>
                <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
                  <MapPin className="h-3.5 w-3.5 text-red-500" /> {city}
                </h3>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {emergencyServices.map((svc) => (
                    <Link
                      key={svc.slug}
                      to={`/urgence/${slugify(svc.artisan ?? svc.slug)}-${slugify(city)}`}
                      className="rounded-full border border-border bg-white px-2.5 py-1 text-[11px] text-slate-600 hover:border-red-300 hover:text-red-600"
                    >
                      Urgence {svc.artisan} {city}
                    </Link>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SEO content */}
        <article className="mt-12 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Urgence travaux : comment réagir ?</h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
            <p>
              Une urgence travaux peut survenir à tout moment : fuite d'eau en pleine nuit, panne électrique, porte claquée...
              Dans ces situations, il est essentiel de pouvoir compter sur un professionnel fiable et disponible rapidement.
            </p>
            <p>
              AfficheServices met à votre disposition un réseau d'artisans vérifiés, disponibles 7 jours sur 7,
              pour intervenir dans les plus brefs délais. Tous nos partenaires sont assurés en responsabilité civile
              professionnelle et s'engagent à fournir un devis avant toute intervention.
            </p>
            <p>
              <strong>Notre engagement :</strong> une mise en relation rapide avec un professionnel qualifié de votre ville,
              un devis transparent avant travaux, et une garantie sur les interventions réalisées. En cas d'urgence,
              utilisez notre formulaire — un artisan vous rappelle sous 30 minutes.
            </p>
          </div>
        </article>

        {/* Final CTA */}
        <div className="mt-8 rounded-2xl bg-gradient-to-br from-red-600 to-red-700 p-8 text-center text-white shadow-lg">
          <h2 className="text-xl font-bold">Une urgence ? Appelez maintenant</h2>
          <p className="mt-2 text-sm text-red-200">Mise en relation immédiate avec un artisan disponible près de chez vous</p>
          <div className="mt-6 flex flex-col items-center gap-3">
            <a
              href="tel:+33743019662"
              className="inline-flex items-center gap-2 rounded-xl bg-white px-8 py-3.5 text-base font-bold text-red-600 shadow-md hover:bg-slate-50"
            >
              <PhoneCall className="h-5 w-5" /> Appeler — 07 43 01 96 62
            </a>
            <Link
              to="/demande-devis/1"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/90 underline-offset-4 hover:underline"
            >
              Ou demander un rappel sous 30 min
            </Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  )
}
